import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { FileText, Plus } from "@phosphor-icons/react";
import { highlightVars, renderVars, fmtDateTime, esc } from "./render";

export function TemplateBody({ t, mode }) {
  const fmt = (v) => (mode === "sample" ? renderVars(v) : highlightVars(v));
  return (
    <div className="bg-white text-black rounded text-sm" data-testid="lead-template-body">
      <div className="px-4 py-2 border-b border-black/10 text-xs"><span className="text-black/50">Objet : </span><span dangerouslySetInnerHTML={{ __html: fmt(esc(t.subject)) }} /></div>
      <div className="p-4 lead-email-render" dangerouslySetInnerHTML={{ __html: fmt(t.body_html) }} />
    </div>
  );
}

export const ModeSwitch = ({ mode, setMode, testid }) => (
  <div className="inline-flex border border-border rounded-md overflow-hidden text-[11px]" data-testid={testid}>
    {[["vars", "Avec variables"], ["sample", "Exemple de rendu"]].map(([k, l]) => (
      <button key={k} onClick={() => setMode(k)} className={`px-2.5 py-1 ${mode === k ? "bg-accent/15 text-accent" : "text-muted hover:text-fg"}`} data-testid={`${testid}-${k}`}>{l}</button>
    ))}
  </div>
);

export function TemplatePicker({ open, onClose, onApply, onCreate, reloadKey }) {
  const [list, setList] = useState(null);
  const [cur, setCur] = useState(null);
  const [mode, setMode] = useState("vars");
  useEffect(() => {
    if (!open) return;
    setList(null);
    api.get("/admin/leads/templates").then(({ data }) => { setList(data.templates); setCur(data.templates[0]?.id || null); }).catch(() => setList([]));
  }, [open, reloadKey]);
  const t = list?.find((x) => x.id === cur);
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="bg-surface border-border w-[95vw] max-w-4xl max-h-[90vh] flex flex-col overflow-hidden" data-testid="lead-template-picker">
        <DialogHeader>
          <DialogTitle>Choisir un template</DialogTitle>
          <DialogDescription>L'objet et le contenu de la campagne seront remplacés. Vos modifications ultérieures ne changeront pas le template d'origine.</DialogDescription>
        </DialogHeader>
        {list === null && <p className="text-sm text-muted animate-pulse">Chargement…</p>}
        {list?.length === 0 && (
          <div className="text-center py-10 space-y-3" data-testid="lead-template-picker-empty">
            <FileText size={28} className="mx-auto text-muted" />
            <p className="text-sm text-fg">Aucun template enregistré pour l'instant.</p>
            <p className="text-xs text-muted">Créez-en un, ou rédigez votre email puis utilisez « Enregistrer comme template ».</p>
            <button onClick={onCreate} className="inline-flex items-center gap-1.5 bg-accent text-black font-semibold rounded-md px-4 py-2 text-sm" data-testid="lead-template-picker-create"><Plus size={14} /> Créer un template</button>
          </div>
        )}
        {list?.length > 0 && (
          <div className="grid md:grid-cols-[230px_minmax(0,1fr)] gap-4 min-h-0 flex-1">
            <div className="space-y-1 overflow-y-auto max-h-[25vh] md:max-h-none pr-1" data-testid="lead-template-picker-list">
              {list.map((x) => (
                <button key={x.id} onClick={() => setCur(x.id)} data-testid={`lead-template-option-${x.id}`}
                  className={`w-full text-left rounded-md border px-3 py-2 text-xs ${x.id === cur ? "border-accent/50 bg-accent/[0.06]" : "border-border hover:border-accent/30"}`}>
                  <div className="text-fg font-medium truncate">{x.name}</div>
                  <div className="text-muted truncate">{x.subject}</div>
                  <div className="text-[10px] text-muted mt-0.5">v{x.version} · {fmtDateTime(x.updated_at)}</div>
                </button>
              ))}
              <button onClick={onCreate} className="w-full text-left text-xs text-accent px-3 py-2 flex items-center gap-1" data-testid="lead-template-picker-new"><Plus size={12} /> Nouveau template</button>
            </div>
            {t && (
              <div className="min-h-0 flex flex-col gap-3" data-testid="lead-template-picker-preview">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <span className="text-xs text-muted">Variables : {t.variables.length ? t.variables.map((v) => `{{${v}}}`).join(" ") : "aucune"}</span>
                  <ModeSwitch mode={mode} setMode={setMode} testid="lead-template-picker-mode" />
                </div>
                <div className="overflow-y-auto min-h-0 flex-1 max-h-[45vh]"><TemplateBody t={t} mode={mode} /></div>
                <button onClick={() => onApply(t)} className="self-start bg-accent text-black font-semibold rounded-md px-4 py-2 text-sm" data-testid="lead-template-apply">Utiliser ce template</button>
              </div>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
